import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  NonNullableFormBuilder,
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ONBOARDING_ENTRY_PATH } from '../../core/onboarding/onboarding-entry';
import { Subscription, timer, Observable } from 'rxjs';
import { map, switchMap, tap, finalize, retryWhen, scan, delayWhen } from 'rxjs/operators';

import { UiService } from '../../core/services/ui.service';
import { User, UserService } from '../../core/services/user.service';
import { AuthService } from '../../core/services/auth.service';
import { PASSWORD_REGEX } from '../../core/constants/validation.constants';
import { AppButtonComponent } from '../../shared/components/button/app-button.component';
import { AuthModalShellComponent } from '../../shared/components/auth-modal-shell/auth-modal-shell.component';
import { AuthPasswordFieldComponent } from '../../shared/components/auth-password-field/auth-password-field.component';
import { SnackbarService } from '../../core/services/snackbar.service';
import { environment } from '../../../environments/environment';
import { getApiBaseUrl } from '../../core/utils/vanity-url.util';

/** Cross-field validator: password === confirmPassword */
export function matchPasswords(group: AbstractControl): ValidationErrors | null {
  const password = group.get('password')?.value;
  const confirm = group.get('confirmPassword')?.value;
  return password === confirm ? null : { mismatch: true };
}

/**
 * Module-level guard for the TLL hosted set-password hand-off. Both ngOnInit subscriptions (modal stream +
 * query params) — and any second component instance — can resolve the same token; without this, each fires
 * its own window.location.assign, sending /auth/set-password twice. The second request then hits an
 * already-consumed token and lands the user on ?error=provisioning_failed. It's module-scoped (not a field)
 * so it also covers two instances; the page navigates away right after, so it resets on the next load.
 */
let hostedSetPasswordRedirectStarted = false;

type CreatePasswordForm = {
  password: FormControl<string>;
  confirmPassword: FormControl<string>;
};

interface SetPasswordResponse {
  email?: string | null;
}

/** Backoff settings */
const MAX_LOGIN_ATTEMPTS = 3; // initial try + 2 retries total
const FIRST_BACKOFF_MS = 2000; // ~2s
const JITTER_MAX_MS = 500; // up to +500ms random jitter
const AFTER_LOGIN_UI_DELAY_MS = 1000; // short UI delay before redirect

@Component({
  selector: 'app-create-password-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AppButtonComponent,
    AuthModalShellComponent,
    AuthPasswordFieldComponent,
  ],
  templateUrl: './create-password-modal.component.html',
  styleUrls: ['./create-password-modal.component.scss'],
})
export class CreatePasswordComponent implements OnInit, OnDestroy {
  isVisible = false;
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  token: string | null = null;

  isFocusPassword = false;
  private subscription = new Subscription();

  // password hints
  validLength = false;
  hasUpperCase = false;
  hasNumber = false;

  createPasswordForm: FormGroup<CreatePasswordForm>;

  constructor(
    private uiService: UiService,
    private userManagementService: UserService, // setUserPassword
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private userService: UserService, // getCurrentUser
    private snackbarService: SnackbarService,
    private fb: NonNullableFormBuilder,
  ) {
    this.createPasswordForm = this.fb.group<CreatePasswordForm>(
      {
        password: this.fb.control('', {
          validators: [Validators.required, Validators.pattern(PASSWORD_REGEX)],
        }),
        confirmPassword: this.fb.control('', {
          validators: [Validators.required],
        }),
      },
      { validators: matchPasswords },
    );

    // track password hints
    this.subscription.add(
      this.createPasswordForm.get('password')!.valueChanges.subscribe((value) => {
        this.checkPasswordHints(value ?? '');
      }),
    );
  }

  ngOnInit(): void {
    // Sync modal visibility + token coming from UiService stream
    this.subscription.add(
      this.uiService.createPasswordModal$.subscribe(({ visible, token }) => {
        if (token && this.redirectToHostedSetPassword(token)) return;
        this.isVisible = visible;
        if (token) this.token = token;
      }),
    );

    // Allow opening via URL ?token=...
    this.subscription.add(
      this.route.queryParams.subscribe((params) => {
        const tokenFromRoute = params['token'];
        if (tokenFromRoute && this.isSetPasswordRoute()) {
          if (this.redirectToHostedSetPassword(tokenFromRoute)) return;
          this.token = tokenFromRoute;
          this.uiService.openCreatePasswordModal(tokenFromRoute); // ensure modal opens with token
        }
      }),
    );
  }

  /**
   * TLL: the invite (welcome) and forgot-password emails set the password on the hosted B2C custom-policy
   * page with a seamless auto-login. Hand off to the backend, which validates the emailed token, mints the
   * signed id_token_hint (email), and redirects into B2C_1A_passwordset. SK12 keeps the in-app modal + ROPC.
   */
  private redirectToHostedSetPassword(token: string): boolean {
    if (!environment.features?.redirectLogin) return false;
    // Fire the hand-off exactly once, even if both ngOnInit subscriptions (or a second instance) resolve a
    // token. Still return true so callers treat it as handled and don't fall through to the in-app modal.
    if (hostedSetPasswordRedirectStarted) return true;
    hostedSetPasswordRedirectStarted = true;
    const base = getApiBaseUrl().replace(/\/$/, '');
    window.location.assign(`${base}/auth/set-password?token=${encodeURIComponent(token)}`);
    return true;
  }

  // ------- UI helpers --------------------------------------------------------

  private checkPasswordHints(password: string): void {
    this.validLength = password.length >= 8;
    this.hasUpperCase = /[A-Z]/.test(password);
    this.hasNumber = /\d/.test(password);
  }

  onPasswordFocus(): void {
    this.isFocusPassword = true;
  }

  onPasswordBlur(): void {
    const pass = this.createPasswordForm.get('password')?.value ?? '';
    this.isFocusPassword = pass.length > 0;
  }

  get isFormInvalid(): boolean {
    return this.createPasswordForm.invalid;
  }

  get confirmMismatchError(): boolean {
    return (
      this.createPasswordForm.hasError('mismatch') &&
      !!this.createPasswordForm.controls.confirmPassword.touched
    );
  }

  // ------- Core logic --------------------------------------------------------

  /**
   * Returns true if an auth error looks transient immediately after password set
   * (AAD B2C eventual consistency -> "invalid username or password" for a few seconds).
   */
  private isTransientAuthError(err: unknown): boolean {
    const apiErr = err as {
      status?: unknown;
      message?: unknown;
      error?: { message?: unknown } | null;
    };
    const msg = String(apiErr?.error?.message ?? apiErr?.message ?? '').toLowerCase();
    const status = typeof apiErr?.status === 'number' ? apiErr.status : null;
    return (
      msg.includes('invalid') || // invalid username/password
      msg.includes('temporar') || // temporary / temporarily
      status === 400 ||
      status === 401
    );
  }

  /**
   * Login with up to 2 retries (total 3 attempts) using exponential backoff and jitter.
   * attempt #0: no delay; #1: ~2s; #2: ~4s
   */
  private loginWithBackoff(email: string, password: string): Observable<unknown> {
    return this.authService.login(email, password).pipe(
      retryWhen((errors) =>
        errors.pipe(
          // count retry attempts and decide whether to continue
          scan(
            (state, err: unknown) => {
              const attempt = state.attempt + 1; // attempt being scheduled now (1..)
              const transient = this.isTransientAuthError(err);
              if (!transient || attempt >= MAX_LOGIN_ATTEMPTS) {
                // not transient or we've already reached the limit -> propagate error
                throw err;
              }
              return { attempt };
            },
            { attempt: 0 },
          ),
          // delay before next retry: 2s, then 4s, with small jitter
          delayWhen((s) => {
            const base = FIRST_BACKOFF_MS * Math.pow(2, s.attempt - 1); // 1->2s, 2->4s
            const jitter = Math.floor(Math.random() * JITTER_MAX_MS); // 0..500ms
            const delayMs = base + jitter;

            // update UI so the user sees what's happening
            this.successMessage = `Finalizing your account… Retrying login (attempt ${s.attempt + 1}/${MAX_LOGIN_ATTEMPTS}) in ${(delayMs / 1000).toFixed(1)}s…`;
            return timer(delayMs);
          }),
        ),
      ),
    );
  }

  onSubmit(): void {
    if (this.createPasswordForm.invalid || !this.token) return;

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const { password } = this.createPasswordForm.getRawValue();

    // SK12 only. TLL never reaches onSubmit — its invite/reset links hand off to the hosted B2C
    // set-password page on init (see redirectToHostedSetPassword), so the form is never shown.
    // Captured by closure so the subscribe `next` handler can pick the right
    // redirect target based on the freshly loaded user profile.
    let loadedUser: User | null = null;

    const flow$ = this.userManagementService.setUserPassword(this.token, password).pipe(
      tap(() => {
        // Show early success so user knows password set is OK
        this.successMessage = 'Password successfully set. Logging you in…';
      }),
      map((response: SetPasswordResponse) => String(response?.email ?? '')),
      switchMap((email) => this.loginWithBackoff(email, password)),
      switchMap(() => this.userService.getCurrentUser()),
      tap((user) => {
        loadedUser = user;
        this.authService.setCurrentUser(user);
        this.authService.loadUserProfile();

        const context = this.uiService.getCreatePasswordContext?.() ?? 'registration';
        if (context === 'reset') {
          this.snackbarService.show('Password updated successfully', 'success');
        } else {
          this.snackbarService.show('Account successfully created. Start learning!', 'success');
        }
      }),
      // small pause to let snackbar render before navigation
      switchMap(() => timer(AFTER_LOGIN_UI_DELAY_MS)),
      finalize(() => {
        this.isLoading = false;
      }),
    );

    this.subscription.add(
      flow$.subscribe({
        next: () => {
          this.createPasswordForm.reset();
          this.closeModal();
          this.router.navigate([this.resolvePostPasswordRoute(loadedUser)]);
        },
        error: (err: unknown) => {
          // prefer meaningful backend message if available
          this.errorMessage = this.readApiErrorMessage(err, 'Operation failed. Please try again.');
        },
      }),
    );
  }

  /**
   * Decides where to land a user after the invite/reset set-password flow finishes.
   * B2B Learners who still have the First UX flag see /onboarding/select-categories;
   * everyone else keeps the legacy /profile/details destination.
   *
   * B2B Group Admins intentionally do NOT trigger here — they see the flow on their
   * first Learner-view toggle instead (see admin-learner-toggle.component.ts).
   */
  private resolvePostPasswordRoute(user: User | null): string {
    const role = (user?.role ?? '').toLowerCase();
    const isLearner = role === 'learner' || role === 'user';
    if (isLearner && user?.shouldTriggerLearnerFirstUX) {
      return ONBOARDING_ENTRY_PATH;
    }
    return '/profile/details';
  }

  private readApiErrorMessage(err: unknown, fallback: string): string {
    const apiErr = err as { error?: { message?: unknown; error?: unknown } | null };
    if (typeof apiErr?.error?.message === 'string' && apiErr.error.message.trim().length > 0) {
      return apiErr.error.message;
    }
    if (typeof apiErr?.error?.error === 'string' && apiErr.error.error.trim().length > 0) {
      return apiErr.error.error;
    }
    return fallback;
  }

  // ------- Lifecycle ---------------------------------------------------------

  closeModal(): void {
    this.uiService.closeCreatePasswordModal();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.uiService.closeCreatePasswordModal();
  }

  private isSetPasswordRoute(): boolean {
    const path =
      typeof window !== 'undefined' && window.location?.pathname
        ? window.location.pathname
        : (this.router.url.split('?')[0] ?? '');
    return path.includes('set-password');
  }
}
